/**
 * JobDetailPage - Shows a single pipeline job's details with inline CV generation.
 * Layout: Left panel (job info + actions) | Right panel (CV generation + cover letter)
 * Replaces the old separate CV Generator page for job-specific generation.
 */

import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePipelineStore } from '@/store/pipeline.store';
import { Button } from '@/components/common';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { ExtensionPrompt } from '@/components/ExtensionPrompt';
import {
  JobDetailHeader,
  StatusActions,
  JobNotes,
  CVGeneratorInline,
  MatchAnalysis,
} from '@/components/job-detail';
import { CoverLetterGeneratorInline } from '@/components/cover-letter';
import '@/styles/job-detail.css';

export default function JobDetailPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const job = usePipelineStore((s) => s.getJobById(jobId ?? ''));
  const updateJob = usePipelineStore((s) => s.updateJob);

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');

  const handleBack = () => {
    navigate('/app/pipeline');
  };

  const handleEditDescription = useCallback(() => {
    setDescriptionDraft(job?.description || '');
    setIsEditingDescription(true);
  }, [job?.description]);

  const handleSaveDescription = useCallback(() => {
    if (job) {
      updateJob(job.id, { description: descriptionDraft.trim() });
      setIsEditingDescription(false);
    }
  }, [job, descriptionDraft, updateJob]);

  // Job not found
  if (!job) {
    return (
      <div className="job-detail">
        <div className="job-detail__not-found">
          <svg
            className="job-detail__not-found-icon"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="job-detail__not-found-heading">Job not found</h2>
          <p className="job-detail__not-found-text">
            This job may have been removed from your pipeline.
          </p>
          <Button onClick={handleBack}>Back to Pipeline</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="job-detail">
      {/* Back link */}
      <button className="job-detail__back-link" onClick={handleBack}>
        <svg
          className="job-detail__back-icon"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        Back to Pipeline
      </button>

      {/* Two-column layout */}
      <div className="job-detail__layout">
        {/* LEFT panel - Job info + actions */}
        <div className="job-detail__left">
          <ErrorBoundary name="JobDetailHeader"><JobDetailHeader job={job} /></ErrorBoundary>
          <ErrorBoundary name="StatusActions"><StatusActions job={job} /></ErrorBoundary>
          <ErrorBoundary name="MatchAnalysis">
            <MatchAnalysis
              jobId={job.id}
              matchScore={job.matchScore}
              hasDescription={!!job.description?.trim()}
            />
          </ErrorBoundary>
          <ErrorBoundary name="JobNotes"><JobNotes jobId={job.id} initialNotes={job.notes} /></ErrorBoundary>

          {/* Job Description section */}
          <div className="job-description">
            <div className="flex justify-between items-center mb-2">
              <h3 className="job-description__title" style={{ marginBottom: 0 }}>Job Description</h3>
              {!isEditingDescription && (
                <Button size="sm" variant="outline" onClick={handleEditDescription}>
                  {job.description ? 'Edit' : 'Add Description'}
                </Button>
              )}
            </div>
            {isEditingDescription ? (
              <div className="space-y-3">
                <textarea
                  rows={8}
                  value={descriptionDraft}
                  onChange={(e) => setDescriptionDraft(e.target.value)}
                  placeholder="Paste the job description here. This enables match analysis and tailored CV generation."
                  className="w-full px-3 py-2 border border-border rounded-md bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                />
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="secondary" onClick={() => setIsEditingDescription(false)}>Cancel</Button>
                  <Button size="sm" onClick={handleSaveDescription}>Save</Button>
                </div>
              </div>
            ) : job.description ? (
              <div className="job-description__content" style={{ whiteSpace: 'pre-wrap', fontSize: '0.875rem', lineHeight: '1.5', color: 'var(--color-text-primary)' }}>
                {job.description}
              </div>
            ) : (
              <div className="space-y-3">
                <p className="job-description__empty">
                  No description yet. Add one to enable match analysis and tailored CV generation.
                </p>
                <ExtensionPrompt variant="no-description" />
              </div>
            )}
          </div>
        </div>

        {/* RIGHT panel - CV generation + Cover Letter */}
        <div className="job-detail__right">
          <ErrorBoundary name="CVGeneratorInline">
            <CVGeneratorInline jobId={job.id} vacancyId={job.vacancyId} />
          </ErrorBoundary>

          <ErrorBoundary name="CoverLetterGeneratorInline">
            <CoverLetterGeneratorInline
              jobId={job.id}
              jobTitle={job.title}
              company={job.company}
              description={job.description}
              jobUrl={job.url}
            />
          </ErrorBoundary>
        </div>
      </div>
    </div>
  );
}
